import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  ManageTagsSection, TagEditDialog, TagsOverviewSection, TagsSnapshot,
} from '../features/tags/TagsPageSections';

function getScoreBadge(score) {
  if (score === null || score === undefined) return { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30', label: 'N/A' };
  if (score >= 90) return { cls: 'score-excellent', label: 'Excellent' };
  if (score >= 75) return { cls: 'score-good', label: 'Good' };
  if (score >= 60) return { cls: 'score-review', label: 'Needs Review' };
  return { cls: 'score-critical', label: 'Critical' };
}

function parseCallDatePart(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const normalized = raw.replace(/\//g, '-');
  let match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  match = normalized.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }

  return null;
}

function parseCallTimePart(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);

  if (!match) {
    return '00:00:00';
  }

  return `${match[1].padStart(2, '0')}:${match[2]}:${match[3] || '00'}`;
}

function getCallSortTimestamp(call) {
  const endStamp = String(call?.end_stamp || '').trim();
  if (endStamp) {
    const normalizedStamp = endStamp.replace(/\//g, '-').replace(' ', 'T');
    const stampTime = new Date(normalizedStamp).getTime();
    if (Number.isFinite(stampTime)) {
      return stampTime;
    }
  }

  const datePart = parseCallDatePart(call?.date);
  if (datePart) {
    const dateTime = new Date(`${datePart}T${parseCallTimePart(call?.time)}`).getTime();
    if (Number.isFinite(dateTime)) {
      return dateTime;
    }
  }

  const fallbackTime = new Date(call?.updated_at || call?.created_at || 0).getTime();
  return Number.isFinite(fallbackTime) ? fallbackTime : 0;
}

function sortCallsByLatestDate(calls) {
  return [...(Array.isArray(calls) ? calls : [])].sort((a, b) => {
    const timestampDiff = getCallSortTimestamp(b) - getCallSortTimestamp(a);
    if (timestampDiff !== 0) {
      return timestampDiff;
    }

    return String(b?.call_id || '').localeCompare(String(a?.call_id || ''));
  });
}

export default function TagsPage() {
  const { isAdmin, isSupervisor } = useAuth();
  const formatIsoDate = (date) => new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
  const createDefaultRange = () => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setMonth(today.getMonth() - 3);
    return {
      from_date: formatIsoDate(fromDate),
      to_date: formatIsoDate(today),
    };
  };
  const formatDateRangeLabel = (range) => {
    if (!range?.from_date || !range?.to_date) {
      return 'Last 3 months';
    }

    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${formatter.format(new Date(range.from_date))} - ${formatter.format(new Date(range.to_date))}`;
  };

  const [dateRange, setDateRange] = useState(createDefaultRange);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [callsLoading, setCallsLoading] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [tagActionId, setTagActionId] = useState(null);
  const [analyzingCalls, setAnalyzingCalls] = useState(new Set());
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [tagCallsPage, setTagCallsPage] = useState(1);
  const [tags, setTags] = useState([]);
  const [summary, setSummary] = useState({ tagged_calls: 0, tags: [] });
  const [selectedTagCalls, setSelectedTagCalls] = useState([]);
  const [selectedTagMeta, setSelectedTagMeta] = useState(null);
  const [selectedTagCallsPages, setSelectedTagCallsPages] = useState(1);
  const [selectedTagCallsTotal, setSelectedTagCallsTotal] = useState(0);
  const [tagSearch, setTagSearch] = useState('');
  const [callFilters, setCallFilters] = useState({ search: '', direction: '', lead_source: '', escalation: '', sort_by: 'created_at', sort_order: 'desc' });
  const [tagForm, setTagForm] = useState({ name: '', keywords: '', is_active: true });
  const callsSectionRef = useRef(null);
  const analyzingCallIdsRef = useRef(new Set());
  const analyzeRefreshTimersRef = useRef(new Map());
  const analyzingCallRowsRef = useRef(new Map());

  const normalizeTagRecord = useCallback((tag, fallbackCount = 0) => ({
    ...tag,
    keywords: Array.isArray(tag?.keywords) ? tag.keywords : [],
    keyword_counts: tag?.keyword_counts && typeof tag.keyword_counts === 'object' ? tag.keyword_counts : {},
    matched_calls: Number(tag?.matched_calls) || 0,
    count: Number(tag?.count ?? fallbackCount) || 0,
  }), []);

  const upsertTagLocally = useCallback((tag, { selectIfEmpty = false } = {}) => {
    if (!tag?.id) {
      return;
    }

    const normalizedTag = normalizeTagRecord(tag);

    setTags((prev) => {
      const next = prev.filter((item) => item.id !== normalizedTag.id);
      next.push(normalizedTag);
      return next;
    });

    setSummary((prev) => {
      const existing = prev.tags.some((item) => item.id === normalizedTag.id);
      const nextTags = existing
        ? prev.tags.map((item) => (item.id === normalizedTag.id
          ? { ...item, ...normalizedTag, count: Number(item.count) || 0 }
          : item))
        : [...prev.tags, { ...normalizedTag, count: 0 }];

      return {
        ...prev,
        tags: nextTags,
      };
    });

    if (selectIfEmpty) {
      setSelectedTagId((prev) => prev || normalizedTag.id);
    }
  }, [normalizeTagRecord]);

  const removeTagLocally = useCallback((tagId) => {
    setTags((prev) => prev.filter((tag) => tag.id !== tagId));
    setSummary((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag.id !== tagId),
    }));
    setSelectedTagId((prev) => (prev === tagId ? null : prev));
  }, []);

  const fetchTags = useCallback(async (refreshKey = '') => {
    const tagsRes = await api.getTags({ refreshKey });
    setTags(tagsRes?.tags || []);
    return tagsRes?.tags || [];
  }, []);

  const fetchSummary = useCallback(async (refreshKey = '') => {
    const summaryRes = await api.getTagsSummary({
      from_date: dateRange.from_date,
      to_date: dateRange.to_date,
      refreshKey,
    });
    const summaryTags = summaryRes?.tags || [];
    const activeSummaryTags = summaryTags.filter((tag) => tag.is_active);
    setSummary({
      tagged_calls: summaryRes?.tagged_calls || 0,
      tags: summaryTags,
    });
    setSelectedTagId((prev) => {
      if (!prev) return null;
      if (activeSummaryTags.length === 0) return null;
      if (prev && activeSummaryTags.some((tag) => tag.id === prev)) return prev;
      return null;
    });
  }, [dateRange.from_date, dateRange.to_date]);

  const fetchPageData = useCallback(async (showSpinner = false, { refreshKey = '' } = {}) => {
    if (!isAdmin && !isSupervisor) return;

    if (showSpinner) setRefreshing(true);
    else setLoading(true);

    try {
      await Promise.all([fetchTags(refreshKey), fetchSummary(refreshKey)]);
    } catch {
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchSummary, fetchTags, isAdmin, isSupervisor]);

  const fetchSelectedTagCalls = useCallback(async ({ silent = false } = {}) => {
    if (!isAdmin && !isSupervisor) {
      setSelectedTagCalls([]);
      setSelectedTagMeta(null);
      setSelectedTagCallsPages(1);
      setSelectedTagCallsTotal(0);
      return;
    }

    if (!silent) {
      setCallsLoading(true);
    }
    try {
      const params = {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
        page: tagCallsPage,
        limit: 10,
        search: callFilters.search,
        direction: callFilters.direction,
        lead_source: callFilters.lead_source,
        sort_by: callFilters.sort_by || 'created_at',
        sort_order: callFilters.sort_order || 'desc',
      };
      if (callFilters.escalation) {
        params.escalation_detected = callFilters.escalation;
      }
      const res = selectedTagId
        ? await api.getTagCalls(selectedTagId, { ...params, keyword: selectedKeyword })
        : await api.getAllTagCalls(params);
      setSelectedTagMeta(res?.tag || null);
      setSelectedTagCalls(res?.calls || []);
      setSelectedTagCallsPages(res?.pages || 1);
      setSelectedTagCallsTotal(res?.total || 0);
      return res;
    } catch {
      toast.error('Failed to load tag-related calls');
      return null;
    } finally {
      if (!silent) {
        setCallsLoading(false);
      }
    }
  }, [callFilters, dateRange.from_date, dateRange.to_date, isAdmin, isSupervisor, selectedKeyword, selectedTagId, tagCallsPage]);

  const clearAnalyzeRefreshTimer = useCallback((callId) => {
    const timerId = analyzeRefreshTimersRef.current.get(callId);
    if (timerId) {
      window.clearTimeout(timerId);
      analyzeRefreshTimersRef.current.delete(callId);
    }
  }, []);

  const refreshAnalyzedCallUntilSettled = useCallback((callId, attempt = 1) => {
    clearAnalyzeRefreshTimer(callId);

    const timerId = window.setTimeout(async () => {
      const [res, callDetail] = await Promise.all([
        fetchSelectedTagCalls({ silent: true }),
        api.getCallDetail(callId).catch(() => null),
      ]);
      const refreshedCall = (res?.calls || []).find((call) => call.call_id === callId);
      const status = String(callDetail?.processing_status || refreshedCall?.processing_status || '').toLowerCase();
      const stillAnalyzing = status === 'analyzing' || status === 'voice_analyzing';

      if (stillAnalyzing && attempt < 20) {
        if (!refreshedCall && analyzingCallRowsRef.current.has(callId)) {
          const preservedCall = {
            ...analyzingCallRowsRef.current.get(callId),
            processing_status: status,
          };
          setSelectedTagCalls((prev) => (
            prev.some((call) => call.call_id === callId)
              ? prev.map((call) => (call.call_id === callId ? { ...call, processing_status: status } : call))
              : sortCallsByLatestDate([preservedCall, ...prev])
          ));
        }

        refreshAnalyzedCallUntilSettled(callId, attempt + 1);
        return;
      }

      analyzingCallRowsRef.current.delete(callId);
      clearAnalyzeRefreshTimer(callId);
    }, 3000);

    analyzeRefreshTimersRef.current.set(callId, timerId);
  }, [clearAnalyzeRefreshTimer, fetchSelectedTagCalls]);

  useEffect(() => { fetchPageData(); }, [fetchPageData]);
  useEffect(() => { setTagCallsPage(1); }, [selectedKeyword, selectedTagId, dateRange.from_date, dateRange.to_date, callFilters]);
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchSelectedTagCalls();
    }
  }, [activeTab, fetchSelectedTagCalls]);
  useEffect(() => {
    if (!selectedKeyword || !selectedTagMeta) {
      return;
    }

    if (!(selectedTagMeta.keywords || []).includes(selectedKeyword)) {
      setSelectedKeyword('');
    }
  }, [selectedKeyword, selectedTagMeta]);
  useEffect(() => () => {
    analyzeRefreshTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    analyzeRefreshTimersRef.current.clear();
    analyzingCallRowsRef.current.clear();
  }, []);

  const resetTagForm = () => {
    setTagForm({ name: '', keywords: '', is_active: true });
    setEditingTagId(null);
  };

  const handleDialogChange = (open) => {
    if (!open && savingTag) {
      return;
    }

    setTagDialogOpen(open);
    if (!open && !savingTag) {
      resetTagForm();
    }
  };

  const openCreateTagDialog = () => {
    resetTagForm();
    setTagDialogOpen(true);
  };

  const handleSaveTag = async (e) => {
    e.preventDefault();
    setSavingTag(true);
    try {
      const payload = editingTagId
        ? { name: tagForm.name, keywords: tagForm.keywords, is_active: tagForm.is_active }
        : { name: tagForm.name, keywords: tagForm.keywords };

      const res = editingTagId
        ? await api.updateTag(editingTagId, payload)
        : await api.createTag(payload);

      if (res?.tag) {
        upsertTagLocally(normalizeTagRecord(res.tag), { selectIfEmpty: !editingTagId });
      }

      toast.success(res.message || (editingTagId ? 'Tag updated' : 'Tag created'));
      setTagDialogOpen(false);
      resetTagForm();
      await fetchPageData(true, { refreshKey: `tag-save-${Date.now()}` });
    } catch (err) {
      toast.error(err?.message || 'Failed to save tag');
    } finally {
      setSavingTag(false);
    }
  };

  const handleEditTag = (tag) => {
    setEditingTagId(tag.id);
    setTagForm({
      name: tag.name || '',
      keywords: Array.isArray(tag.keywords) ? tag.keywords.join('\n') : '',
      is_active: Boolean(tag.is_active),
    });
    setTagDialogOpen(true);
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? This will remove it from matched calls as well.`)) {
      return;
    }

    setTagActionId(tag.id);
    try {
      const res = await api.deleteTag(tag.id);
      toast.success(res.message || 'Tag deleted');
      if (editingTagId === tag.id) {
        setTagDialogOpen(false);
        resetTagForm();
      }
      removeTagLocally(tag.id);
      await fetchPageData(true, { refreshKey: `tag-delete-${Date.now()}` });
    } catch (err) {
      toast.error(err?.message || 'Failed to delete tag');
    } finally {
      setTagActionId(null);
    }
  };

  // const handleAnalyzeCall = async (e, callId, rowKey = callId) => {
  //   e.stopPropagation();
  //   if (analyzingCallIdsRef.current.has(callId)) {
  //     return;
  //   }

  //   analyzingCallIdsRef.current.add(callId);
  //   const currentCall = selectedTagCalls.find((call) => call.call_id === callId);
  //   if (currentCall) {
  //     analyzingCallRowsRef.current.set(callId, currentCall);
  //     setSelectedTagCalls((prev) => prev.map((call) => (
  //       call.call_id === callId ? { ...call, processing_status: 'analyzing' } : call
  //     )));
  //   }
  //   setAnalyzingCalls((prev) => new Set(prev).add(rowKey));
  //   try {
  //     const res = await api.analyzeCall(callId);
  //     toast.success(res.message || 'Analysis started');
  //     refreshAnalyzedCallUntilSettled(callId);
  //   } catch (err) {
  //     toast.error(err?.message || 'Failed to start analysis');
  //   } finally {
  //     analyzingCallIdsRef.current.delete(callId);
  //     setAnalyzingCalls((prev) => {
  //       const next = new Set(prev);
  //       next.delete(rowKey);
  //       return next;
  //     });
  //   }
  // };

  const handleSelectTag = (tagId, keyword = '') => {
    setSelectedTagId(tagId);
    setSelectedKeyword(keyword);
    window.setTimeout(() => {
      callsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  if (!isAdmin && !isSupervisor) {
    return <div className="text-center py-12 text-muted-foreground">Access required</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeTags = tags.filter((tag) => tag.is_active).length;
  const filteredSummaryTags = [...summary.tags]
    .filter((tag) => tag.is_active)
    .filter((tag) => {
      const query = tagSearch.trim().toLowerCase();
      if (!query) return true;
      const keywordMatch = (tag.keywords || []).some((keyword) => String(keyword || '').toLowerCase().includes(query));
      return tag.name.toLowerCase().includes(query) || keywordMatch;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  const sortedTags = [...tags].sort((a, b) => {
    const leftDate = new Date(a.created_at || 0).getTime() || 0;
    const rightDate = new Date(b.created_at || 0).getTime() || 0;
    if (rightDate !== leftDate) {
      return rightDate - leftDate;
    }
    if (Boolean(b.is_active) !== Boolean(a.is_active)) {
      return Number(Boolean(b.is_active)) - Number(Boolean(a.is_active));
    }
    return a.name.localeCompare(b.name);
  });
  const allTagRecords = tags.reduce((totalRecords, tag) => totalRecords + (Number(tag.matched_calls) || 0), 0);
  const maxManagedMatches = sortedTags.reduce((max, tag) => Math.max(max, tag.matched_calls || 0), 0);
  return (
    <div className="space-y-4">
      <div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tags</h1>
          <p className="text-sm text-muted-foreground mt-1">Review tag activity and matched calls by date range.</p>
        </div>
      </div>

      <TagsSnapshot
        totalTags={tags.length}
        activeTags={activeTags}
        taggedCalls={allTagRecords}
      />

      <div className="flex border-b border-border">
        {[
          { id: 'overview', label: 'Tag Overview' },
          ...(isAdmin ? [{ id: 'manage', label: 'Manage Tags' }] : []),
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <TagsOverviewSection
          dateRange={dateRange}
          isCustomDateRange={isCustomDateRange}
          onDateRangeChange={(nextRange) => {
            setIsCustomDateRange(true);
            setDateRange((prev) => ({
              from_date: nextRange.from_date || prev.from_date,
              to_date: nextRange.to_date || nextRange.from_date || prev.to_date,
            }));
          }}
          onDateRangeClear={() => {
            setDateRange(createDefaultRange());
            setIsCustomDateRange(false);
          }}
          refreshing={refreshing}
          onRefresh={() => fetchPageData(true)}
          tagSearch={tagSearch}
          setTagSearch={setTagSearch}
          filteredSummaryTags={filteredSummaryTags}
          hasAnySummaryTags={summary.tags.some((tag) => tag.is_active)}
          selectedTagId={selectedTagId}
          onSelectTag={handleSelectTag}
          selectedTagMeta={selectedTagMeta}
          selectedKeyword={selectedKeyword}
          selectedTagCallsTotal={selectedTagCallsTotal}
          dateRangeLabel={isCustomDateRange ? formatDateRangeLabel(dateRange) : 'Last 3 months'}
          callFilters={callFilters}
          setCallFilters={setCallFilters}
          callsLoading={callsLoading}
          selectedTagCalls={selectedTagCalls}
          getScoreBadge={getScoreBadge}
          analyzingCalls={analyzingCalls}
          // onAnalyzeCall={handleAnalyzeCall}
          selectedTagCallsPages={selectedTagCallsPages}
          tagCallsPage={tagCallsPage}
          setTagCallsPage={setTagCallsPage}
          callsSectionRef={callsSectionRef}
        />
      )}

      {activeTab === 'manage' && (
        <ManageTagsSection
          sortedTags={sortedTags}
          maxManagedMatches={maxManagedMatches}
          onOpenCreateTagDialog={openCreateTagDialog}
          onEditTag={handleEditTag}
          onDeleteTag={handleDeleteTag}
          tagActionId={tagActionId}
        />
      )}

      <TagEditDialog
        open={tagDialogOpen}
        onOpenChange={handleDialogChange}
        editingTagId={editingTagId}
        tagForm={tagForm}
        setTagForm={setTagForm}
        savingTag={savingTag}
        onSaveTag={handleSaveTag}
      />
    </div>
  );
}
